/* eslint-disable import/no-unresolved */
import React, { useState, useEffect, useRef } from "react"
import { useStaticQuery, graphql } from "gatsby"
import styled from "styled-components"
import tw from "twin.macro"
import { CSSTransition } from "react-transition-group"

import { CustomHeading } from "@templates"
import { KEY_CODES } from "@utils"
import scrollReveal from "@utils/scroll.reveal"
import { srConfig } from "@config"

const StyledJobs = styled.section`
  ${tw`min-h-full text-suvaGrey flex flex-col justify-center mb-12`}
  & {
    .custom-heading,
    .inner {
      ${tw`px-8`}
    }

    .inner {
      ${tw`mt-8 flex flex-col gap-10 md:grid md:grid-cols-3 md:gap-0`}
    }
  }
`
const StyledTabList = styled.ul`
  ${tw`list-none p-0 m-0 relative w-auto md:w-max z-10 flex flex-row md:flex-col col-auto md:col-span-1 overflow-auto`}
  scrollbar-width: none;

  &::-webkit-scrollbar {
    height: 0;
  }
`

const StyledTabButton = styled.button`
  ${tw`bg-transparent border-0 h-10 flex items-center text-center border-b-2 outline-none appearance-none cursor-pointer whitespace-nowrap w-full px-4 md:px-0`}
  ${({ isActive }) => (isActive ? tw`text-deepSky` : tw`text-suvaGrey`)}

  @media (min-width: 768px) {
    min-width: var(--tab-width);
    ${tw`text-left w-full border-b-0 border-l-2 px-4`}
  }

  &:hover,
  &:focus {
    background-color: var(--light-deepSky);
  }
`

const StyledHighlight = styled.div`
  ${tw`absolute top-auto bottom-0 w-full bg-deepSky rounded-lg`}
  max-width: var(--tab-width);
  height: 0px;
  transform: ${({ activeTabId }) =>
    `translateX(calc(${activeTabId} * var(--tab-width)))`};
  transition: transform 0.25s cubic-bezier(0.645, 0.045, 0.355, 1);
  transition-delay: 0.1s;

  @media (min-width: 768px) {
    ${tw`h-10 z-20 top-0 left-0`}
    width: 2px;
    transform: ${({ activeTabId }) =>
      `translateY(calc(${activeTabId} * var(--tab-height)))`};
  }
`

const StyledTabContent = styled.div`
  ${tw`outline-none md:col-span-2`}
  & {
    h3 {
      ${tw`mt-0 font-medium`}
      a {
        ${tw`text-burntSiena`}
      }
    }

    .job-detail--1 {
      ${tw`flex items-center gap-x-6`}

      .range,
      .type {
        ${tw`uppercase text-sm tracking-widest mt-0`}
      }

      .type {
        ${tw`text-green-500 italic font-bold`}
      }
    }

    .rawHTML {
      ${tw`m-0 mt-8 text-sm`}
      ul {
        ${tw`mt-0 p-0 list-none w-full`}

        li {
          ${tw`relative pl-8 mb-3`}
          &:before {
            content: "";
            position: absolute;
            top: 5px;
            left: 0;
            display: block;
            width: 0px;
            height: 0px;
            border-top: 5px solid transparent;
            border-bottom: 5px solid transparent;
            border-right: 5px solid transparent;
            border-left: 5px solid var(--suvaGrey);
          }
        }
      }
    }
  }
`

const Jobs = () => {
  const data = useStaticQuery(graphql`
    query {
      jobs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/jobs/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              company
              location
              range
              url
              type
            }
            html
          }
        }
      }
    }
  `)

  const jobData = data.jobs.edges

  const [activeTabId, setActiveTabId] = useState(0)
  const [tabFocus, setTabFocus] = useState(null)
  const tabs = useRef([])

  const revealContainer = useRef(null)
  useEffect(() => scrollReveal.reveal(revealContainer.current, srConfig()), [])

  const focusTab = () => {
    if (tabs.current[tabFocus]) {
      tabs.current[tabFocus].focus()
      return
    }
    // If we're at the end, go to the start
    if (tabFocus >= tabs.current.length) {
      setTabFocus(0)
    }
    // If we're at the start, move to the end
    if (tabFocus < 0) {
      setTabFocus(tabs.current.length - 1)
    }
  }

  // Only re-run the effect if tabFocus changes
  useEffect(() => focusTab(), [tabFocus])

  // Focus on tabs when using up & down arrow keys
  const onKeyDown = e => {
    if (e.key === KEY_CODES.ARROW_UP || e.key === KEY_CODES.ARROW_DOWN) {
      e.preventDefault()
      // Move up
      if (e.key === KEY_CODES.ARROW_UP) {
        setTabFocus(tabFocus - 1)
      }
      // Move down
      if (e.key === KEY_CODES.ARROW_DOWN) {
        setTabFocus(tabFocus + 1)
      }
    }
  }

  return (
    <StyledJobs id="jobs" ref={revealContainer}>
      <CustomHeading
        title="Experiences"
        subTitle="What all experiences do I have? Let's look into it."
      />
      <div className="inner">
        <StyledTabList aria-label="Job tabs" onKeyDown={onKeyDown}>
          {jobData &&
            jobData.map(({ node }, i) => {
              const { company } = node.frontmatter
              return (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i}>
                  <StyledTabButton
                    isActive={activeTabId === i}
                    onClick={() => setActiveTabId(i)}
                    // eslint-disable-next-line no-return-assign
                    ref={el => (tabs.current[i] = el)}
                    id={`tab-${i}`}
                    role="tab"
                    aria-selected={() => activeTabId === i}
                    aria-controls={`panel-${i}`}
                    tabIndex={activeTabId === i ? "0" : "-1"}
                  >
                    <span>{company}</span>
                  </StyledTabButton>
                </li>
              )
            })}
          <StyledHighlight activeTabId={activeTabId} />
        </StyledTabList>

        {jobData &&
          jobData.map(({ node }, i) => {
            const { frontmatter, html } = node
            const { title, url, company, range, type } = frontmatter

            return (
              <CSSTransition
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                in={activeTabId === i}
                timeout={250}
                classNames="fade"
              >
                <StyledTabContent
                  id={`panel-${i}`}
                  role="tabpanel"
                  tabIndex={activeTabId === i ? "0" : "-1"}
                  aria-labelledby={`tab-${i}`}
                  aria-hidden={activeTabId !== i}
                  hidden={activeTabId !== i}
                >
                  <h3>
                    <span>{title}</span>
                    <span className="company">
                      &nbsp;@&nbsp;
                      <a
                        href={url}
                        target="_blank"
                        className="inline-link"
                        rel="noopener noreferrer"
                      >
                        {company}
                      </a>
                    </span>
                  </h3>
                  <div className="job-detail--1">
                    <p className="range">{range}</p>
                    <p className="type">({type})</p>
                  </div>

                  <div
                    className="rawHTML"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                </StyledTabContent>
              </CSSTransition>
            )
          })}
      </div>
    </StyledJobs>
  )
}

export default Jobs
